<script setup lang="ts">
import { useSlots } from 'vue';
import type { PreviewEmptyStateProps } from './preview-empty-state';

/**
 * An empty state laid over a faded preview of the real content.
 *
 * The default slot holds the preview (a table filled with sample rows, say).
 * It is shown at full opacity at the top, fades out towards the bottom and is
 * inert, so nothing in it invites a click. The title, description and actions
 * sit in the faded region, which is where the eye lands after reading the
 * first rows. Without a default slot the component is a plain bordered empty
 * state, so a page can offer to hide the preview and keep the same message.
 *
 * `#description` replaces the description prop when the text needs markup,
 * such as an inline icon button. `#hint` is secondary information that sits
 * above the actions on wider screens and moves below them on phones, so the
 * call to action stays as high as possible where space is scarce.
 *
 * On phones the preview is also cut short (the first row readable, the second
 * fading) and the text starts below it instead of overlapping, because the
 * header and tabs above already take most of a small screen.
 */
const props = defineProps<PreviewEmptyStateProps>();

const slots = useSlots();
// Read on every render rather than in a computed: slots are not reactive, so
// a computed would freeze the answer from the first render and the component
// would never switch to its bordered form when the parent drops the preview.
const hasPreview = (): boolean => slots.default !== undefined;
</script>

<template>
  <section
    class="flex min-w-0 flex-col"
    :class="
      !hasPreview() && 'rounded-lg border border-slate-200 bg-slate-50 p-8'
    "
  >
    <!-- inert keeps the preview's own buttons (a real table's cells, say) out
         of the tab order; pointer-events and select cover older browsers. -->
    <div
      v-if="hasPreview()"
      class="pointer-events-none min-w-0 mask-b-from-55% mask-b-to-85% select-none max-sm:max-h-40 max-sm:overflow-hidden max-sm:mask-b-to-100%"
      aria-hidden="true"
      inert
    >
      <slot />
    </div>

    <div
      class="flex flex-col items-center gap-3 text-center"
      :class="
        hasPreview() && 'relative px-4 pb-4 max-sm:px-2 max-sm:pt-2 sm:-mt-16'
      "
    >
      <span
        v-if="props.badge"
        class="inline-flex items-center rounded-full bg-slate-800 px-2 py-1 text-xs font-semibold text-white"
      >
        {{ props.badge }}
      </span>
      <h3 class="text-lg font-semibold text-slate-900 max-sm:text-base">
        {{ props.title }}
      </h3>
      <div
        v-if="props.description || slots.description"
        class="flex max-w-xl flex-col gap-2 text-sm text-slate-600"
      >
        <slot name="description">
          <p>{{ props.description }}</p>
        </slot>
      </div>
      <!-- Same 8px spacing as a second description paragraph on wider screens;
           after the actions on phones. -->
      <div
        v-if="slots.hint"
        class="max-w-xl text-sm text-slate-600 max-sm:order-last sm:-mt-1"
      >
        <slot name="hint" />
      </div>
      <div
        v-if="slots.actions"
        class="mt-1 flex flex-wrap items-center justify-center gap-2"
      >
        <slot name="actions" />
      </div>
    </div>
  </section>
</template>
